import { useCallback, useId, useMemo } from 'react';

import { Box, Checkbox, Group, Stack } from '@mantine/core';
import { IconPlus } from 'ui-icons';

import { ButtonMantine } from '../../../../Buttons/ButtonMantine';
import { Select } from '../../../../Combobox';
import { Alert } from '../../../../Feedback/Alert/Alert';
import { Text } from '../../../../Typography/Text/Text';
import { ConditionalWrapper } from '../../../../Utils/ConditionalWrapper';
import { DraggableWrapper } from '../../dragAndDrop/DraggableWrapper';
import { DroppableWrapper } from '../../dragAndDrop/DroppableWrapper';
import {
  AdditionalFieldsProvider,
  CheckboxOverride,
  useAdditionalFieldsContext,
} from '../../forms/AdditionalFieldsForm/AdditionalFields.context';
import {
  AdditionalFieldIOption_Internal,
  AdditionalFieldOption,
} from '../../forms/AdditionalFieldsForm/AdditionalFields.types';
import { FieldLabel } from './addAdditionalFieldsFields/FieldLabel';
import { SelectionOptions } from './addAdditionalFieldsFields/SelectionOptions';
import { ADDITIONAL_FIELD_OPTIONS } from './constants';
import { translateFormValues, useAddAdditionalFields } from './useAddAdditionalFields';

type AdditionalFieldsProps = {
  /**
   * The title of the form
   */
  title?: string;
  options?: AdditionalFieldOption[];
  isOrderable?: boolean;
  isDisabled?: boolean;
  onChange?: (options: AdditionalFieldOption[]) => void;
  formId?: string;

  allowDuplicateNames?: boolean;
  /**
   * Exclude certain options from the list of available options, does not affect existing options
   */
  excludedOptions?: AdditionalFieldOption['type'][];
  /**
   * Allow custom buttons to show up next to the Add Field button
   */
  secondaryAction?: React.ReactNode;
  /**
   * Override the existing required checkbox for the field if needed, should affect the label, what additional field property is being checked, and the render of the checkbox
   */
  checkboxOverride?: CheckboxOverride;
};

const FieldValue = ({ index, isReadOnly }: { index: number; isReadOnly: boolean }) => {
  const {
    excludedOptions,
    getInputProps,
    addSelectOption,
    getOptionsProps,
    getRemoveSelectOption,
    isOrderable,
    isDisabled,
    handleUpdateSelectionOptionOrder,
    optionErrorHandler,
    checkboxOverride,
  } = useAdditionalFieldsContext();

  const checkboxInputProps = getInputProps(index, checkboxOverride?.propName ?? 'required');
  const typeInputProps = getInputProps(index, 'type');
  const optionsInputProps = getInputProps(index, 'options');

  const _getOptionIndex = getOptionsProps(index);
  const removeSelectOption = getRemoveSelectOption(index);

  const _handleUpdate = useCallback(
    (from: number, toIdx: number) => {
      return handleUpdateSelectionOptionOrder({ from, to: toIdx, index });
    },
    [handleUpdateSelectionOptionOrder, index]
  );

  const fields = useMemo(
    () => ADDITIONAL_FIELD_OPTIONS.filter((value) => !excludedOptions?.includes(value.value)),
    [excludedOptions]
  );

  const selectErrorState = optionErrorHandler(index)?.error;

  return (
    <Stack>
      <Group gap="sm" wrap="nowrap" grow style={{ flexGrow: 1 }}>
        <Select
          w="100%"
          data={fields}
          placeholder="Select Field"
          maw="50%"
          readOnly={isDisabled || isReadOnly}
          {...typeInputProps}
        />
        {checkboxOverride?.renderCheckbox ? (
          checkboxOverride.renderCheckbox(
            <Checkbox
              style={{ textWrap: 'nowrap' }}
              disabled={isDisabled || isReadOnly}
              label={checkboxOverride.label}
              {...checkboxInputProps}
              defaultChecked={!!checkboxInputProps.value}
            />
          )
        ) : (
          <Checkbox
            style={{ textWrap: 'nowrap' }}
            label="Make Required"
            disabled={isDisabled || isReadOnly}
            {...checkboxInputProps}
            defaultChecked={!!checkboxInputProps.value}
          />
        )}
      </Group>
      <Alert.Collapse in={!!selectErrorState} type="error" title={selectErrorState} />
      {(typeInputProps.value === 'dropdown' || typeInputProps.value === 'multiselect') && (
        <SelectionOptions
          onRemoveOption={removeSelectOption}
          options={optionsInputProps.value}
          onAddOption={() => addSelectOption(index)}
          getOptionsProps={_getOptionIndex}
          isOrderable={isOrderable}
          handleUpdateOrder={_handleUpdate}
          isDisabled={isDisabled || isReadOnly}
        />
      )}
    </Stack>
  );
};

const Field = ({ index }: { index: number }) => {
  const { getInputProps, removeItem } = useAdditionalFieldsContext();

  const isReadOnly = getInputProps(index, 'isEditable').value === false;

  return (
    <Stack style={{ flexGrow: 1 }} bg="white" gap="sm">
      <FieldLabel
        index={index}
        onRemove={removeItem}
        isInputDisabled={isReadOnly}
        {...getInputProps(index, 'name')}
      />
      <FieldValue index={index} isReadOnly={isReadOnly} />
    </Stack>
  );
};

/** Conditionally wraps Field in a draggable and applies the field handle */
const FieldOrderingWrapper = ({ index }: { index: number }) => {
  const { isOrderable, isDisabled } = useAdditionalFieldsContext();
  return (
    <ConditionalWrapper
      condition={!!isOrderable}
      wrapper={(children) => (
        <DraggableWrapper
          key={index}
          index={index}
          draggableId={index.toString()}
          // NOTE: Normally the default Group props would align the handle to the perfectly centered, however
          // This component can have a drop down under the 2nd field, so we need to manually align the handle to the top
          groupProps={{
            align: 'flex-start',
          }}
          handleProps={{
            mt: '35px',
            isDraggable: !isDisabled,
          }}
        >
          {children}
        </DraggableWrapper>
      )}
    >
      <Field index={index} />
    </ConditionalWrapper>
  );
};

const Fields = ({ options }: { options: AdditionalFieldIOption_Internal[] }) => {
  return (
    <Stack bg="white">
      {options.map((option, index) => (
        <FieldOrderingWrapper key={option.key} index={index} />
      ))}
    </Stack>
  );
};

const FieldsOrderingWrapper = ({ options }: { options: AdditionalFieldIOption_Internal[] }) => {
  const id = useId();
  const { isOrderable, handleUpdateOrder } = useAdditionalFieldsContext();
  return (
    <ConditionalWrapper
      condition={!!isOrderable}
      wrapper={(children) => (
        <DroppableWrapper
          handleUpdateOrder={handleUpdateOrder}
          droppableId={`additionalFields${id}`}
        >
          {children}
        </DroppableWrapper>
      )}
    >
      <Fields options={options} />
    </ConditionalWrapper>
  );
};

const Title = ({ title }: Pick<AdditionalFieldsProps, 'title'>) => {
  // This box keeps spacing
  if (!title) return <Box />;
  return (
    <Text fz="sm" fw={500}>
      {title}
    </Text>
  );
};

const AddField = () => {
  const { addNewField, isDisabled } = useAdditionalFieldsContext();
  return (
    <ButtonMantine size="sm" leftSection={<IconPlus />} onClick={addNewField} disabled={isDisabled}>
      Add
    </ButtonMantine>
  );
};

const AddAdditionalFieldsForm = ({
  title,
  options,
  isOrderable = true,
  isDisabled = false,
  onChange,
  formId,
  allowDuplicateNames = false,
  excludedOptions,
  secondaryAction,
  checkboxOverride,
}: AdditionalFieldsProps) => {
  const additionalFields = useAddAdditionalFields({
    initialOptions: options,
    onChange,
    formId,
    allowDuplicateNames,
    excludedOptions,
  });

  return (
    <AdditionalFieldsProvider
      value={{ ...additionalFields, isOrderable, isDisabled, checkboxOverride }}
    >
      <Stack gap="lg" w="100%">
        <Group justify="space-between">
          <Title title={title} />
          <Group gap="sm">
            {secondaryAction}
            <AddField />
          </Group>
        </Group>
        {additionalFields.formOptions.length >= 1 && (
          <FieldsOrderingWrapper options={additionalFields.formOptions} />
        )}
      </Stack>
    </AdditionalFieldsProvider>
  );
};

/**
 * A wrapper for AdditionalFieldsForm
 *  Allowing optional Options to be passed
 */
const AddAdditionalFieldsFormGuard = ({ ...props }: Partial<AdditionalFieldsProps>) => {
  return <AddAdditionalFieldsForm {...props} />;
};
AddAdditionalFieldsForm.Field = Field;
/** Retranslates the form values back into the expected type: This is useful when using FormGod to pull the direct form data */
AddAdditionalFieldsForm.translateFormValues = translateFormValues;

export { AddAdditionalFieldsForm, AddAdditionalFieldsFormGuard };
